import { Clapperboard, Image as ImageIcon, Type, Video, Volume2, type LucideIcon } from "lucide-react"
import { useCallback, useMemo } from "react"
import { SearchableCombobox, type SearchableComboboxOption } from "@/components/ui/searchable-combobox"
import { catalogModelComboboxOption } from "@/lib/generation-model-option"
import { Checkbox, Input, Textarea } from "@/components/ui/primitives"
import { FieldHintTooltip } from "@/components/ui/field-hint-tooltip"
import { useTranslation } from "@/hooks/use-translation"
import type { GenerationModelCatalogEntry, GenerationModelsByContext } from "@/types/workspace"

export type GenerationModelOverrideContext =
  | "agent"
  | "storyboard"
  | "storyboard_correction"
  | "location_reference"
  | "character_reference"
  | "character_outfit"
  | "cover"
  | "shot_video"
  | "video_correction"
  | "voice_tts"
  | "voice_creation"
  | "audio_transcription"

export type GenerationModelExtraSettings = {
  openrouter_video_resolution: string
  happyhorse_video_resolution: string
  byteplus_seedance_video_resolution: string
  byteplus_seedance_generate_audio: boolean
  byteplus_seedance_draft_default: boolean
  kling_generate_audio: boolean
  location_variant_kling_image_fidelity: string
  character_outfit_kling_image_fidelity: string
  seedance_animatic_reference_block: string
  generation_image_sizes: Record<string, string>
}

export const DEFAULT_GENERATION_MODEL_EXTRA_SETTINGS: GenerationModelExtraSettings = {
  openrouter_video_resolution: "480p",
  happyhorse_video_resolution: "720P",
  byteplus_seedance_video_resolution: "720p",
  byteplus_seedance_generate_audio: false,
  byteplus_seedance_draft_default: false,
  kling_generate_audio: true,
  location_variant_kling_image_fidelity: "",
  character_outfit_kling_image_fidelity: "",
  seedance_animatic_reference_block: "",
  generation_image_sizes: {},
}

export function applyGenerationModelOverride(
  overrides: Record<string, string>,
  context: GenerationModelOverrideContext,
  modelKey: string,
) {
  const nextOverrides = { ...overrides }
  if (modelKey) {
    nextOverrides[context] = modelKey
  } else {
    delete nextOverrides[context]
  }
  return nextOverrides
}

type ModelRow = {
  context: GenerationModelOverrideContext
  labelKey:
    | "providers.video"
    | "providers.videoCorrection"
    | "providers.text"
    | "providers.storyboardImages"
    | "providers.frameCorrection"
    | "providers.locations"
    | "providers.characters"
    | "providers.characterOutfits"
    | "providers.cover"
    | "providers.voiceTts"
    | "providers.voiceCreation"
    | "providers.audioTranscription"
}

type ModelGroup = {
  id: "video" | "text" | "image" | "audio"
  icon: LucideIcon
  rows: ModelRow[]
}

type GenerationModelOverridesFieldsProps = {
  generationModelsByContext?: GenerationModelsByContext
  overrides: Record<string, string>
  effectiveModelKeys?: Record<string, string | null | undefined>
  contexts?: GenerationModelOverrideContext[]
  extraSettings: GenerationModelExtraSettings
  onOverrideChange: (context: GenerationModelOverrideContext, modelKey: string) => void
  onExtraSettingsChange: (partial: Partial<GenerationModelExtraSettings>) => void
  showAnimaticPrompt?: boolean
}

export const MODEL_GROUPS: ModelGroup[] = [
  {
    id: "video",
    icon: Video,
    rows: [
      { context: "shot_video", labelKey: "providers.video" },
      { context: "video_correction", labelKey: "providers.videoCorrection" },
    ],
  },
  {
    id: "image",
    icon: ImageIcon,
    rows: [
      { context: "storyboard", labelKey: "providers.storyboardImages" },
      { context: "storyboard_correction", labelKey: "providers.frameCorrection" },
      { context: "location_reference", labelKey: "providers.locations" },
      { context: "character_reference", labelKey: "providers.characters" },
      { context: "character_outfit", labelKey: "providers.characterOutfits" },
      { context: "cover", labelKey: "providers.cover" },
    ],
  },
  {
    id: "audio",
    icon: Volume2,
    rows: [
      { context: "voice_tts", labelKey: "providers.voiceTts" },
      { context: "voice_creation", labelKey: "providers.voiceCreation" },
      { context: "audio_transcription", labelKey: "providers.audioTranscription" },
    ],
  },
  {
    id: "text",
    icon: Type,
    rows: [{ context: "agent", labelKey: "providers.text" }],
  },
]

const ALL_CONTEXTS = MODEL_GROUPS.flatMap((group) => group.rows.map((row) => row.context))

const IMAGE_SIZE_CONTEXTS = new Set<GenerationModelOverrideContext>([
  "storyboard",
  "storyboard_correction",
  "location_reference",
  "character_reference",
  "character_outfit",
  "cover",
])

export function SectionDivider({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <div className="flex items-center gap-3 pt-3 pb-1" aria-hidden>
      <div className="h-px flex-1 bg-ui-border" />
      <Icon className="h-5 w-5 shrink-0 text-ui-text-muted" strokeWidth={1.75} />
      <div className="h-px flex-1 bg-ui-border" />
    </div>
  )
}

function extraParam(model: GenerationModelCatalogEntry | undefined, key: string) {
  return model?.extra_params?.find((param) => param.key === key)
}

function showAnimaticBlock(model: GenerationModelCatalogEntry | undefined) {
  if (!model) return false

  return (
    model.provider === "byteplus_seedance_video" ||
    model.provider === "openrouter_video" ||
    model.provider === "kling" ||
    model.id.includes("seedance") ||
    model.id.includes("kling")
  )
}

export function GenerationModelOverridesFields({
  generationModelsByContext = {},
  overrides,
  effectiveModelKeys = {},
  contexts = ALL_CONTEXTS,
  extraSettings,
  onOverrideChange,
  onExtraSettingsChange,
  showAnimaticPrompt = true,
}: GenerationModelOverridesFieldsProps) {
  const { t } = useTranslation(["workspace", "common"])
  const visibleContexts = useMemo(() => new Set(contexts), [contexts])
  const visibleGroups = useMemo(
    () =>
      MODEL_GROUPS.map((group) => ({
        ...group,
        rows: group.rows.filter((row) => visibleContexts.has(row.context)),
      })).filter((group) => group.rows.length > 0),
    [visibleContexts],
  )

  const modelsFor = useCallback(
    (context: GenerationModelOverrideContext) => generationModelsByContext[context] ?? [],
    [generationModelsByContext],
  )

  const effectiveKey = useCallback(
    (context: GenerationModelOverrideContext) => overrides[context] || effectiveModelKeys[context] || "",
    [effectiveModelKeys, overrides],
  )

  const studioDefaultLabel = useCallback(
    (context: GenerationModelOverrideContext) => {
      const key = effectiveModelKeys[context]
      if (!key) return null
      return modelsFor(context).find((model) => model.id === key)?.label ?? key
    },
    [effectiveModelKeys, modelsFor],
  )

  const selectedVideoModel = useMemo(
    () => modelsFor("shot_video").find((model) => model.id === effectiveKey("shot_video")),
    [effectiveKey, modelsFor],
  )
  const selectedLocationModel = useMemo(
    () => modelsFor("location_reference").find((model) => model.id === effectiveKey("location_reference")),
    [effectiveKey, modelsFor],
  )
  const selectedOutfitModel = useMemo(
    () => modelsFor("character_outfit").find((model) => model.id === effectiveKey("character_outfit")),
    [effectiveKey, modelsFor],
  )

  const videoResolutionParam = extraParam(selectedVideoModel, "resolution")
  const videoAudioParam = extraParam(selectedVideoModel, "generate_audio")
  const videoProvider = selectedVideoModel?.provider ?? ""
  const resolutionValue =
    videoProvider === "openrouter_video"
      ? extraSettings.openrouter_video_resolution
      : videoProvider === "happyhorse_video"
        ? extraSettings.happyhorse_video_resolution
        : extraSettings.byteplus_seedance_video_resolution
  const audioChecked =
    videoProvider === "kling" ? extraSettings.kling_generate_audio : extraSettings.byteplus_seedance_generate_audio
  const defaultOptionLabel = t("providers.defaultFromStudio")

  const setResolution = (value: string) => {
    if (videoProvider === "openrouter_video") {
      onExtraSettingsChange({ openrouter_video_resolution: value })
    } else if (videoProvider === "happyhorse_video") {
      onExtraSettingsChange({ happyhorse_video_resolution: value })
    } else {
      onExtraSettingsChange({ byteplus_seedance_video_resolution: value })
    }
  }

  const setAudio = (checked: boolean) => {
    if (videoProvider === "kling") {
      onExtraSettingsChange({ kling_generate_audio: checked })
    } else {
      onExtraSettingsChange({ byteplus_seedance_generate_audio: checked })
    }
  }

  const comboboxOptions = (context: GenerationModelOverrideContext): SearchableComboboxOption[] => {
    const models = [...modelsFor(context)].sort((a, b) => {
      const aEnabled = a.enabled !== false
      const bEnabled = b.enabled !== false
      if (aEnabled !== bEnabled) return aEnabled ? -1 : 1
      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    })

    const platformDisabledLabel = t("common:platformDisabledModel")
    return [
      { value: "", label: defaultOptionLabel },
      ...models.map((model) => catalogModelComboboxOption(model, platformDisabledLabel)),
    ]
  }

  return (
    <div className="space-y-4">
      {visibleGroups.map((group) => (
        <div key={group.id}>
          <SectionDivider icon={group.icon} />
          <div className="mt-2 space-y-4">
            {group.rows.map(({ context, labelKey }) => {
              const inheritedLabel = !overrides[context] ? studioDefaultLabel(context) : null
              const imageSizeParam = IMAGE_SIZE_CONTEXTS.has(context)
                ? extraParam(
                    modelsFor(context).find((model) => model.id === effectiveKey(context)),
                    "image_size",
                  )
                : undefined
              const imageSizeOptions = imageSizeParam?.options ?? []
              const imageSizeDefault = String(imageSizeParam?.default ?? imageSizeOptions[0] ?? "")
              const storedImageSize = extraSettings.generation_image_sizes?.[context] ?? ""
              const imageSizeValue =
                storedImageSize && imageSizeOptions.includes(storedImageSize) ? storedImageSize : imageSizeDefault

              const setImageSize = (value: string) => {
                const next = { ...(extraSettings.generation_image_sizes ?? {}) }
                if (!value || value === imageSizeDefault) {
                  delete next[context]
                } else {
                  next[context] = value
                }
                onExtraSettingsChange({ generation_image_sizes: next })
              }

              const clearImageSizeIfInvalid = (modelKey: string) => {
                if (!IMAGE_SIZE_CONTEXTS.has(context)) return
                const stored = extraSettings.generation_image_sizes?.[context]
                if (!stored) return
                const modelId = modelKey || effectiveModelKeys[context] || ""
                const options =
                  extraParam(
                    modelsFor(context).find((model) => model.id === modelId),
                    "image_size",
                  )?.options ?? []
                if (options.includes(stored)) return
                const next = { ...(extraSettings.generation_image_sizes ?? {}) }
                delete next[context]
                onExtraSettingsChange({ generation_image_sizes: next })
              }

              return (
                <div key={context} className="space-y-2">
                  <label className="block text-xs font-medium text-ui-text-secondary">{t(labelKey)}</label>
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <SearchableCombobox
                        value={overrides[context] ?? ""}
                        onChange={(value) => {
                          onOverrideChange(context, value)
                          clearImageSizeIfInvalid(value)
                        }}
                        options={comboboxOptions(context)}
                        placeholder={defaultOptionLabel}
                      />
                    </div>
                    {imageSizeOptions.length > 0 ? (
                      <SearchableCombobox
                        className="w-24 shrink-0"
                        value={imageSizeValue}
                        onChange={setImageSize}
                        options={imageSizeOptions.map((option) => ({ value: option, label: option }))}
                      />
                    ) : null}
                  </div>
                  {inheritedLabel ? (
                    <p className="text-[11px] text-ui-text-muted">
                      {t("providers.studioDefaultNow", { model: inheritedLabel })}
                    </p>
                  ) : null}

                  {context === "shot_video" && videoResolutionParam ? (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-ui-text-secondary">
                        {t("providers.resolution")}
                      </label>
                      <SearchableCombobox
                        value={resolutionValue}
                        onChange={setResolution}
                        options={(videoResolutionParam.options ?? []).map((option) => ({
                          value: option,
                          label: option,
                        }))}
                        placeholder={t("providers.resolution")}
                      />
                    </div>
                  ) : null}

                  {context === "shot_video" && videoAudioParam ? (
                    <label className="flex items-center gap-2 text-xs text-ui-text-secondary">
                      <Checkbox checked={audioChecked} onChange={(event) => setAudio(event.target.checked)} />
                      <span className="inline-flex items-center gap-1">
                        {t("providers.generateSyncedAudio")}
                        {videoProvider !== "kling" ? (
                          <FieldHintTooltip label={t("providers.generateSyncedAudioHint")} />
                        ) : null}
                      </span>
                    </label>
                  ) : null}

                  {context === "shot_video" && videoProvider === "byteplus_seedance_video" ? (
                    <label className="flex items-center gap-2 text-xs text-ui-text-secondary">
                      <Checkbox
                        checked={extraSettings.byteplus_seedance_draft_default}
                        onChange={(event) =>
                          onExtraSettingsChange({ byteplus_seedance_draft_default: event.target.checked })
                        }
                      />
                      <span>{t("providers.seedanceDraftDefault")}</span>
                    </label>
                  ) : null}

                  {context === "location_reference" && selectedLocationModel?.provider === "kling_image" ? (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-ui-text-secondary">
                        {t("providers.klingVariantFidelity")}
                      </label>
                      <Input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={extraSettings.location_variant_kling_image_fidelity}
                        onChange={(event) =>
                          onExtraSettingsChange({ location_variant_kling_image_fidelity: event.target.value })
                        }
                        placeholder={t("providers.defaultFromStudioShort")}
                      />
                    </div>
                  ) : null}

                  {context === "character_outfit" &&
                  (selectedOutfitModel?.provider === "kling_image" || selectedOutfitModel?.id.includes("kling")) ? (
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-ui-text-secondary">
                        {t("providers.klingOutfitFidelity")}
                      </label>
                      <Input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={extraSettings.character_outfit_kling_image_fidelity}
                        onChange={(event) =>
                          onExtraSettingsChange({ character_outfit_kling_image_fidelity: event.target.value })
                        }
                        placeholder={t("providers.defaultFromStudioShort")}
                      />
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {showAnimaticPrompt && visibleContexts.has("shot_video") && showAnimaticBlock(selectedVideoModel) ? (
        <div>
          <SectionDivider icon={Clapperboard} />
          <div className="mt-2 space-y-2">
            <label className="block text-xs font-medium text-ui-text-secondary">
              {t("providers.animaticPromptBlock")}
            </label>
            <Textarea
              className="min-h-[88px]"
              value={extraSettings.seedance_animatic_reference_block}
              onChange={(event) => onExtraSettingsChange({ seedance_animatic_reference_block: event.target.value })}
              placeholder={t("providers.animaticPromptPlaceholder")}
            />
            <p className="text-[11px] text-ui-text-secondary">{t("providers.animaticPromptHint")}</p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
